import React, { FC, RefObject, useEffect, useLayoutEffect, useState } from 'react';
import Konva from 'konva';
import { Layer, Stage } from 'react-konva';
import useImage from 'use-image';
import PageSpinner from 'components/PageSpinner/PageSpinner';
import ImageLayer from '../ImageLayer';
import ReferenceTag from '../ReferenceTag';
import { StageContainer } from './ImageGeneratorStage.styles';

interface ReferenceImageProps {
  imageUrl: string;
  maxWidth: number;
  maxHeight: number;
  tags: Tag[];
  stageRef: RefObject<Konva.Stage>;
  selectedTag?: number;
  setSelectedTag: (tag?: number) => void;
  updateImageSize: (height: number, width: number) => void;
  onImageLoadError: () => void;
  updateTag: (tag: number, x: number, y: number, text: string, width: number, height: number) => void;
}

export interface Tag {
  tag: number;
  x: number;
  y: number;
  text: string;
  width?: number;
  height?: number;
}

interface Dimensions {
  width: number;
  height: number;
}

// Fits Child in Parent
const getFitScaling = (parent: Dimensions, child: Dimensions): number =>
  Math.min(parent.width / child.width, parent.height / child.height);

const ReferenceImageStage: FC<ReferenceImageProps> = ({
  imageUrl,
  maxWidth,
  maxHeight,
  stageRef,
  tags,
  selectedTag,
  setSelectedTag,
  updateImageSize,
  onImageLoadError,
  updateTag,
}) => {
  const [image, imageStatus] = useImage(imageUrl);
  const [width, setWidth] = useState(maxWidth);
  const [height, setHeight] = useState(maxHeight);
  const [tagState, setTagState] = useState<'idle' | 'editing' | 'transforming'>('idle');

  const checkDeselect = (e: any) => {
    // deselect when clicked on background image
    const clickedOnEmpty = 'background-image' === e.target.getLayer().attrs.id;
    if (clickedOnEmpty) {
      setSelectedTag(undefined);
      setTagState('idle');
    }
  };

  const onShapeSelect = (tag: number) => {
    // Set isTransforming and the tag No. selected
    setSelectedTag(tag);
    setTagState('transforming');
  };

  const onShapeDoubleClick = (tag: number) => {
    // Set isEditing and the tag No. selected
    setSelectedTag(tag);
    setTagState('editing');
  };

  useLayoutEffect(() => {
    if (image) {
      const scale = getFitScaling({ width: maxWidth, height: maxHeight }, { width: image.width, height: image.height });
      setWidth(image.width * scale);
      setHeight(image.height * scale);
    }
  }, [maxWidth, maxHeight, image]);

  useEffect(() => {
    if (imageStatus === 'failed') onImageLoadError();
  }, [imageStatus, onImageLoadError]);

  useLayoutEffect(() => {
    updateImageSize(height, width);
  }, [height, width, updateImageSize]);

  return image ? (
    <StageContainer>
      <Stage width={width} height={height} onMouseDown={checkDeselect} onTouchStart={checkDeselect} ref={stageRef}>
        <ImageLayer image={image} height={height} width={width} />
        {tags.length ? (
          <Layer id="reference-tags">
            {tags.map(tag => (
              <ReferenceTag
                tag={tag.tag}
                text={tag.text}
                isSelected={selectedTag === tag.tag}
                isEditing={tagState === 'editing'}
                isTransforming={tagState === 'transforming'}
                onSelect={onShapeSelect}
                onDoubleClick={onShapeDoubleClick}
                key={tag.tag}
                updateTag={updateTag}
                initialX={tag.x}
                initialY={tag.y}
                initialWidth={tag.width}
                initialHeight={tag.height}
              />
            ))}
          </Layer>
        ) : null}
      </Stage>
    </StageContainer>
  ) : (
    <PageSpinner />
  );
};

export default ReferenceImageStage;
